/**
 * Rolled Steel Tee Bar Section Data
 * Indian Standard Tee Bars (ISNT)
 * 
 * Data source: ROLLED STEEL TEE BARS dimensions and properties.csv
 * 
 * Essential Variables (8 total):
 * 1. h - Overall depth/height (mm)
 * 2. b - Width of flange (mm)
 * 3. tf - Thickness of flange (mm)
 * 4. tw - Thickness of web (mm)
 * 5. r1 - Root radius at web-flange junction (mm)
 * 6. r2 - Toe radius at flange edges (mm)
 * 7. D - Slope of flange (degrees)
 * 8. Cxx - Centroid from bottom of web (cm)
 */

export interface RolledSteelTeeBarData {
  designation: string;
  h: number;            // Overall depth (mm)
  b: number;            // Width of flange (mm)
  tf: number;           // Thickness of flange (mm)
  tw: number;           // Thickness of web (mm)
  r1: number;           // Root radius (mm)
  r2: number;           // Toe radius (mm)
  D: number;            // Slope of flange (degrees)
  Cxx: number;          // Centroid from bottom (cm)
  w: number;            // Weight (kg/m)
  a: number;            // Area (cm²)
  Ixx: number;          // Moment of Inertia X-X (cm⁴)
  Iyy: number;          // Moment of Inertia Y-Y (cm⁴)
  rxx: number;          // Radius of Gyration X-X (cm)
  ryy: number;          // Radius of Gyration Y-Y (cm)
  Zxx: number;          // Section Modulus X-X (cm³)
  Zyy: number;          // Section Modulus Y-Y (cm³)
}

/**
 * Database of Rolled Steel Tee Bar sections
 * Using first 3 sections from CSV for testing
 */
export const rolledSteelTeeBars: RolledSteelTeeBarData[] = [
  {
    designation: 'ISNT 20',
    h: 20,
    b: 20,
    tf: 3,
    tw: 3,
    r1: 4,
    r2: 3,
    D: 91,
    Cxx: 0.6,
    w: 0.9,
    a: 1.13,
    Ixx: 0.4,
    Iyy: 0.2,
    rxx: 0.59,
    ryy: 0.39,
    Zxx: 0.3,
    Zyy: 0.2
  },
  {
    designation: 'ISNT 30',
    h: 30,
    b: 30,
    tf: 3,
    tw: 3,
    r1: 5,
    r2: 3.5,
    D: 91,
    Cxx: 0.83,
    w: 1.4,
    a: 1.75,
    Ixx: 1.4,
    Iyy: 0.6,
    rxx: 0.89,
    ryy: 0.57,
    Zxx: 0.6,
    Zyy: 0.4
  },
  {
    designation: 'ISNT 40',
    h: 40,
    b: 40,
    tf: 6,
    tw: 6,
    r1: 5.5,
    r2: 4,
    D: 91,
    Cxx: 1.2,
    w: 3.5,
    a: 4.48,
    Ixx: 6.3,
    Iyy: 3,
    rxx: 1.18,
    ryy: 0.82,
    Zxx: 2.2,
    Zyy: 1.5
  }
];

/**
 * Get Tee Bar section data by designation
 */
export const getRolledSteelTeeBarByDesignation = (designation: string): RolledSteelTeeBarData | undefined => {
  return rolledSteelTeeBars.find(section => section.designation === designation);
};

/**
 * Get all available Tee Bar designations
 */
export const getRolledSteelTeeBarDesignations = (): string[] => {
  return rolledSteelTeeBars.map(section => section.designation);
};
